import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { MdPeople, MdAdd, MdRemove } from "react-icons/md";
import { turnoApi } from "../../services/api/turnoApi";

const formatFecha = (fecha) => {
  if (!fecha) return "—";
  const parte = String(fecha).split("T")[0];
  const [y, m, d] = parte.split("-");
  if (!y || !m || !d) return fecha;
  return `${d}/${m}/${y}`;
};

const ModalCompletarDetalleTurno = ({
  turnoSeleccionado,
  isOpen,
  onClose,
  showToast,
}) => {
  const [cantidadObjetivo, setCantidadObjetivo] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const cantidadActual = Number(turnoSeleccionado?.cantidad_comensales ?? 0);

  useEffect(() => {
    if (!isOpen || !turnoSeleccionado) return;
    setCantidadObjetivo(String(cantidadActual));
    setResultado(null);
  }, [isOpen, turnoSeleccionado?.id, cantidadActual]);

  const handleClose = (reload = false) => {
    setResultado(null);
    setCantidadObjetivo("");
    onClose(reload);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnoSeleccionado?.id) {
      showToast?.("error", "Seleccione un turno");
      return;
    }

    const objetivo = parseInt(cantidadObjetivo, 10);
    if (Number.isNaN(objetivo) || objetivo < 0) {
      showToast?.("error", "Ingrese una cantidad válida (0 o mayor)");
      return;
    }

    if (objetivo === cantidadActual) {
      showToast?.("info", "La cantidad objetivo es igual a la actual. No hay cambios.");
      return;
    }

    setLoading(true);
    try {
      const response = await turnoApi.completarDetalle({
        id_turno_cabecera: turnoSeleccionado.id,
        cantidad_objetivo: objetivo,
      });
      const data = response.data || {};
      setResultado(data);
      if (data.success !== false) {
        showToast?.("success", data.message || "Detalle actualizado");
      } else {
        showToast?.("error", data.message || "No se pudo completar el detalle");
      }
    } catch (error) {
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Error al completar detalle del turno";
      showToast?.("error", typeof msg === "string" ? msg : "Error al completar detalle");
    } finally {
      setLoading(false);
    }
  };

  const diferencia =
    cantidadObjetivo !== "" && !Number.isNaN(parseInt(cantidadObjetivo, 10))
      ? parseInt(cantidadObjetivo, 10) - cantidadActual
      : 0;

  return (
    <>
      <style>
        {`
          .modal-completar-detalle .ant-modal-content {
            border-radius: 20px;
            overflow: hidden;
          }
          .modal-completar-detalle .ant-modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-bottom: none;
            padding: 1.25rem 1.5rem;
          }
          .modal-completar-detalle .ant-modal-title {
            color: white;
            font-weight: 700;
          }
          .modal-completar-detalle .ant-modal-close {
            color: white;
          }
          .btn-dh-primary-modal {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
          }
          .btn-dh-secondary-modal {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
          }
          .dh-form-control {
            border-radius: 12px;
            border: 2px solid #e5e7eb;
            padding: 0.5rem 0.75rem;
            width: 100%;
          }
          .tabla-resultado {
            font-size: 0.85rem;
            max-height: 220px;
            overflow-y: auto;
          }
        `}
      </style>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MdPeople size={22} />
            Completar detalle
          </div>
        }
        open={isOpen}
        onCancel={() => handleClose(!!resultado)}
        footer={null}
        width={720}
        className="modal-completar-detalle"
        destroyOnClose
      >
        {turnoSeleccionado && (
          <div
            style={{
              background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
              border: "2px solid #3b82f6",
              borderRadius: 12,
              padding: "0.75rem 1rem",
              color: "#1e40af",
              marginBottom: "1rem",
              fontSize: "0.9rem",
            }}
          >
            <strong>Turno:</strong> {turnoSeleccionado.comedor?.nombre || "N/A"} —{" "}
            {turnoSeleccionado.tipo_servicio_alimentacion?.descripcion || "N/A"} —{" "}
            {formatFecha(turnoSeleccionado.fecha_turno)}
            <br />
            <strong>Cantidad actual:</strong> {cantidadActual} comensal(es)
            <br />
            <small>
              Si la cantidad es mayor, se toman comensales del <strong>día anterior</strong>;
              si no alcanza, del <strong>anteayer</strong> y días previos (mismo
              proyecto, comedor y servicio), hasta completar el objetivo.
            </small>
          </div>
        )}

        {!resultado ? (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1rem" }}>
              <label className="dh-form-label" style={{ fontWeight: 600 }}>
                Cantidad objetivo
              </label>
              <input
                type="number"
                min={0}
                className="dh-form-control"
                value={cantidadObjetivo}
                onChange={(e) => setCantidadObjetivo(e.target.value)}
                disabled={loading}
                required
              />
              {diferencia !== 0 && cantidadObjetivo !== "" && (
                <div style={{ marginTop: 8, fontSize: "0.85rem" }}>
                  {diferencia > 0 ? (
                    <span style={{ color: "#059669" }}>
                      <MdAdd /> Se agregarán hasta {diferencia} comensal(es) desde
                      ayer, anteayer u otros días previos (que no estén ya en este turno).
                    </span>
                  ) : (
                    <span style={{ color: "#dc2626" }}>
                      <MdRemove /> Se eliminarán {Math.abs(diferencia)}{" "}
                      comensal(es) (priorizando los que no estaban ayer).
                    </span>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn-dh-secondary-modal"
                onClick={() => handleClose(false)}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-dh-primary-modal"
                disabled={loading || !turnoSeleccionado}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Procesando...
                  </>
                ) : (
                  "Aplicar"
                )}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div
              style={{
                background:
                  resultado.accion === "eliminar"
                    ? "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)"
                    : "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
                borderRadius: 12,
                padding: "0.75rem 1rem",
                marginBottom: "1rem",
                fontSize: "0.9rem",
              }}
            >
              {resultado.message}
              <div style={{ marginTop: 6 }}>
                <strong>Cantidad final:</strong> {resultado.cantidad_final}{" "}
                (objetivo: {resultado.cantidad_objetivo})
                {resultado.fecha_turno_dia_anterior && (
                  <>
                    {" "}
                    · Orígenes:{" "}
                    {(resultado.fechas_origen_usadas || [resultado.fecha_turno_dia_anterior])
                      .map(formatFecha)
                      .join(", ")}
                  </>
                )}
              </div>
            </div>

            {(resultado.agregados || []).length > 0 && (
              <div style={{ marginBottom: "1rem" }}>
                <h6 style={{ color: "#059669", fontWeight: 700 }}>
                  Agregados ({resultado.agregados.length})
                </h6>
                <div className="tabla-resultado">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Documento</th>
                        <th>Nombre</th>
                        <th>Cliente</th>
                        <th>Origen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.agregados.map((c) => (
                        <tr key={`a-${c.id_comensal}`}>
                          <td style={{ fontFamily: "monospace" }}>
                            {c.documento || "—"}
                          </td>
                          <td>{c.nombre || "—"}</td>
                          <td>{c.cliente || "—"}</td>
                          <td>{formatFecha(c.fecha_origen) || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(resultado.eliminados || []).length > 0 && (
              <div style={{ marginBottom: "1rem" }}>
                <h6 style={{ color: "#dc2626", fontWeight: 700 }}>
                  Eliminados ({resultado.eliminados.length})
                </h6>
                <div className="tabla-resultado">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Documento</th>
                        <th>Nombre</th>
                        <th>Cliente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.eliminados.map((c) => (
                        <tr key={`e-${c.id_comensal}-${c.id_turno_detalle}`}>
                          <td style={{ fontFamily: "monospace" }}>
                            {c.documento || "—"}
                          </td>
                          <td>{c.nombre || "—"}</td>
                          <td>{c.cliente || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(resultado.omitidos || []).length > 0 && (
              <div
                style={{
                  background: "#fef3c7",
                  border: "1px solid #f59e0b",
                  borderRadius: 8,
                  padding: "0.75rem",
                  fontSize: "0.85rem",
                  color: "#92400e",
                  marginBottom: "1rem",
                }}
              >
                {(resultado.omitidos[0].motivo ||
                  "No se pudo completar toda la cantidad solicitada.")}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn-dh-primary-modal"
                onClick={() => handleClose(true)}
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ModalCompletarDetalleTurno;
